import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, TrendingUp, Target, Check, Utensils, ArrowUpCircle, Package } from "lucide-react";
import api, { euro } from "../lib/api";

const SEEN_KEY = "qcost_seen_alerts";
const loadSeen = () => { try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); } catch (e) { console.error("seen alerts read failed", e); return new Set(); } };
const saveSeen = (keys) => { try { localStorage.setItem(SEEN_KEY, JSON.stringify(keys)); } catch (e) { console.error("seen alerts save failed", e); } };

export const NotificationBell = () => {
  const [data, setData] = useState({ low_margin: [], price_alerts: [], count: 0, threshold: 20 });
  const [seen, setSeen] = useState(loadSeen);
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const navigate = useNavigate();

  const load = () => api.get("/notifications").then(({ data }) => setData(data)).catch((e) => console.error("notifications load failed", e));
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onClick); };
  }, []);

  const margins = (data.low_margin || []).map((p) => ({ ...p, key: p.key || `margin:${p.id}` }));
  const prices = (data.price_alerts || []).map((a) => ({ ...a, key: a.key || `${a.type}:${a.product}:${a.supplier}:${a.current_date}` }));
  const rises = (data.price_rise_alerts || []).map((a) => ({ ...a, key: a.key || `rise:${a.normalized_name}:${a.supplier}:${a.current_date}` }));
  const recipesA = (data.recipe_alerts || []).map((a) => ({ ...a, key: a.key || `recipe:${a.id}` }));
  const compA = (data.composite_alerts || []).map((a) => ({ ...a, key: a.key || `composite-fc:${a.id}` }));
  const allKeys = [...margins, ...prices, ...rises, ...recipesA, ...compA].map((i) => i.key);
  const unseen = allKeys.filter((k) => !seen.has(k)).length;
  const total = allKeys.length;

  const openDropdown = () => {
    const next = !open;
    setOpen(next);
    if (next && total > 0) { const ns = new Set(allKeys); setSeen(ns); saveSeen([...ns]); }
  };
  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={openDropdown}
        className="relative w-10 h-10 rounded-full border border-stone-200 bg-white flex items-center justify-center text-stone-600 hover:bg-stone-50 hover:text-brand transition-colors">
        <Bell size={18} strokeWidth={1.6} />
        {unseen > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-alert text-white text-[10px] font-bold flex items-center justify-center">
            {unseen > 99 ? "99+" : unseen}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-96 bg-white border border-stone-200 rounded-sm shadow-lg z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">Avvisi</span>
            <span className="text-xs text-stone-400">{total} totali</span>
          </div>

          {total === 0 ? (
            <div className="px-4 py-8 text-center text-stone-400">
              <Check size={26} className="mx-auto mb-2 text-brand" />
              <p className="text-sm">Nessun avviso attivo. Ottimo!</p>
            </div>
          ) : (
            <div className="max-h-[30rem] overflow-auto">
              {margins.length > 0 && (
                <div>
                  <div className="px-4 py-1.5 bg-stone-50 text-[10px] uppercase tracking-wide font-bold text-stone-500 flex items-center gap-1.5">
                    <AlertTriangle size={11} className="text-alert" /> Margine basso ({margins.length})
                  </div>
                  {margins.slice(0, 8).map((p) => (
                    <button key={p.key} data-testid={`notif-item-${p.id}`} onClick={() => go("/marginalita")}
                      className="w-full text-left px-4 py-2.5 hover:bg-stone-50 flex items-start gap-2.5 border-b border-stone-100">
                      <AlertTriangle size={14} className="text-alert mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-stone-800 truncate">{p.name}</p>
                        <p className="text-xs text-stone-500">Margine <b className="text-alert">{p.margin_pct}%</b>
                          {p.sale_price != null && p.best_purchase != null && <> · {euro(p.best_purchase)} → {euro(p.sale_price)}</>}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {prices.length > 0 && (
                <div>
                  <div className="px-4 py-1.5 bg-stone-50 text-[10px] uppercase tracking-wide font-bold text-stone-500 flex items-center gap-1.5">
                    <TrendingUp size={11} className="text-brand-accent" /> Prezzi ({prices.length})
                  </div>
                  {prices.slice(0, 8).map((a) => {
                    const isTarget = a.type === "target";
                    return (
                      <button key={a.key} data-testid="notif-price-item" onClick={() => go("/avvisi")}
                        className="w-full text-left px-4 py-2.5 hover:bg-stone-50 flex items-start gap-2.5 border-b border-stone-100">
                        {isTarget ? <Target size={14} className="text-brand-accent mt-0.5 shrink-0" /> : <TrendingUp size={14} className="text-alert mt-0.5 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-stone-800 truncate">{a.product}</p>
                          <p className="text-xs text-stone-500">{isTarget ? "Supera obiettivo" : "Aumento"} <b className="text-alert">+{a.change_pct}%</b> · {a.supplier}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {rises.length > 0 && (
                <div>
                  <div className="px-4 py-1.5 bg-stone-50 text-[10px] uppercase tracking-wide font-bold text-stone-500 flex items-center gap-1.5">
                    <ArrowUpCircle size={11} className="text-alert" /> Rincari acquisti ({rises.length})
                  </div>
                  {rises.slice(0, 8).map((a) => (
                    <button key={a.key} data-testid="notif-rise-item" onClick={() => go("/avvisi")}
                      className="w-full text-left px-4 py-2.5 hover:bg-stone-50 flex items-start gap-2.5 border-b border-stone-100">
                      <ArrowUpCircle size={14} className="text-alert mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-stone-800 truncate">{a.product}</p>
                        <p className="text-xs text-stone-500">Sopra media <b className="text-alert">+{a.change_pct}%</b>
                          {a.avg_price != null && a.current_price != null && <> · media {euro(a.avg_price)} → {euro(a.current_price)}</>} · {a.supplier}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {recipesA.length > 0 && (
                <div>
                  <div className="px-4 py-1.5 bg-stone-50 text-[10px] uppercase tracking-wide font-bold text-stone-500 flex items-center gap-1.5">
                    <Utensils size={11} className="text-alert" /> Food cost piatti ({recipesA.length})
                  </div>
                  {recipesA.slice(0, 8).map((a) => (
                    <button key={a.key} data-testid="notif-recipe-item" onClick={() => go("/ricette")}
                      className="w-full text-left px-4 py-2.5 hover:bg-stone-50 flex items-start gap-2.5 border-b border-stone-100">
                      <Utensils size={14} className="text-alert mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-stone-800 truncate">{a.name}</p>
                        <p className="text-xs text-stone-500">{a.rise_pct != null ? <>Rincaro <b className="text-alert">+{a.rise_pct}%</b> vs mese prec.</> : <>Food cost <b className="text-alert">{a.food_cost_pct}%</b> (soglia {a.threshold}%)</>}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {compA.length > 0 && (
                <div>
                  <div className="px-4 py-1.5 bg-stone-50 text-[10px] uppercase tracking-wide font-bold text-stone-500 flex items-center gap-1.5">
                    <Package size={11} className="text-indigo-600" /> Food cost composti ({compA.length})
                  </div>
                  {compA.slice(0, 8).map((a) => (
                    <button key={a.key} data-testid="notif-composite-item" onClick={() => go("/anagrafica")}
                      className="w-full text-left px-4 py-2.5 hover:bg-stone-50 flex items-start gap-2.5 border-b border-stone-100">
                      <Package size={14} className="text-indigo-600 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-stone-800 truncate">{a.name}</p>
                        <p className="text-xs text-stone-500">Food cost <b className="text-alert">{a.food_cost_pct}%</b> (soglia {a.threshold}%)
                          {a.best_purchase != null && a.sale_price != null && <> · {euro(a.best_purchase)} → {euro(a.sale_price)}</>}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex border-t border-stone-200">
            <button data-testid="notif-view-margins" onClick={() => go("/marginalita")}
              className="flex-1 text-center py-2.5 text-xs font-semibold text-brand hover:bg-stone-50 border-r border-stone-200">Marginalità</button>
            <button data-testid="notif-view-alerts" onClick={() => go("/avvisi")}
              className="flex-1 text-center py-2.5 text-xs font-semibold text-brand hover:bg-stone-50">Avvisi prezzi</button>
          </div>
        </div>
      )}
    </div>
  );
};
